import { uniq } from "lodash-es";
import { SupabaseService } from "../../../server/supabase/base-supabase-service";

import type { Database } from "../../../__definitions__/database-definitions";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";
import type { SerializedDesignHuddleNode } from "../../message/editor/nodes/design-huddle-node";
import type { Segment } from "../../segment/types";
import type { PostgrestResponse, PostgrestSingleResponse } from "@supabase/postgrest-js";
import type { SerializedEditorState, SerializedLexicalNode } from "lexical";

export type MomentPreviewTags = Database["public"]["Functions"]["get_preview_data"]["Returns"];

type MomentPreviewTestData = Database["public"]["Functions"]["get_preview_used_fields"]["Returns"];

export type PreviewDataRecipient =
  Database["public"]["Functions"]["get_preview_data_segment_json_for_connection"]["Returns"][number];

type DynamicSegmentCounts =
  Database["public"]["Functions"]["get_dynamic_segment_counts_segment_json"]["Returns"][number];

export class SupabaseMomentPreviewService extends SupabaseService<"moment"> {
  table = "moment" as const;

  async getDynamicSegmentRecipients(
    account_id: UUID,
    segment_id_: UUID,
    channel: NonNullable<ChannelName>,
  ): Promise<
    PostgrestResponse<
      Database["public"]["Functions"]["get_persons_in_segment_json"]["Returns"][number]
    >
  > {
    return this.log_errors(
      await this.getAllFromSelection(
        account_id,
        this.client.rpc("get_persons_in_segment_json", {
          segment_id_,
          channels_: [channel],
          account_id_: account_id,
        }),
        {
          order: [{ column: "email", dir: "asc" }],
        },
      ),
    );
  }

  async getDynamicSegmentCountsSegmentJson(
    segment_json: Segment["segment_json"],
    account_id: UUID,
    dynamic_segment_ids_: UUID[],
    target_manager_: boolean,
    target_connection_type_?: string,
  ): Promise<PostgrestResponse<DynamicSegmentCounts>> {
    return this.log_errors(
      await this.getAllFromSelection(
        null,
        this.client.rpc("get_dynamic_segment_counts_segment_json", {
          segment_json,
          account_id_: account_id,
          dynamic_segment_ids_,
          target_manager_,
          target_connection_type_,
        }),
        {
          order: [{ column: "id", dir: "asc" }],
        },
      ),
    );
  }

  async getMomentPreviewTestData(
    design: SerializedEditorState | null,
    email_subject: string | null,
    additional_merge_fields: string[] = [],
  ): Promise<PostgrestSingleResponse<MomentPreviewTestData>> {
    const merge_fields_ = previewMergeFields(design, email_subject, additional_merge_fields);

    return this.log_errors(this.client.rpc("get_preview_used_fields", { merge_fields_ }));
  }

  async getMomentPreview(
    subjectId: UUID,
    recipientId: UUID,
    design: SerializedEditorState | null,
    email_subject: string | null,
    additional_merge_fields: string[],
  ): Promise<PostgrestSingleResponse<MomentPreviewTags>> {
    const merge_fields_ = previewMergeFields(design, email_subject, additional_merge_fields);

    return this.log_errors(
      this.client.rpc("get_preview_data", {
        recipient_id_: recipientId,
        subject_id_: subjectId,
        merge_fields_,
      }),
    );
  }
}

export const previewMergeFields = (
  design: SerializedEditorState | null,
  email_subject: string | null,
  additional_merge_fields: string[] = [],
): string[] =>
  uniq([
    ...(design ? extractMergeFields(design.root) : []),
    ...extractEmailSubjectMergeFields(email_subject),
    ...additional_merge_fields,
  ]);

export function extractMergeFields(node: SerializedLexicalNode): string[] {
  //Start with merge tag v2 fields
  const tags: string[] = [];

  try {
    tags.push(...extractMergeTagV2Fields(node));
  } catch {
    // Ignore errors from merge tag v2 extraction
  }

  // Add non-merge tag v2 fields
  try {
    const stringed = JSON.stringify(node || {}) || "";
    const mergeTagRegex = /\{\{([\w-]+(?:\.[\w-]+)?)\}\}/g;

    tags.push(...(stringed.match(mergeTagRegex) || []));
  } catch {
    // Ignore errors from non-merge tag v2 extraction
  }

  const sanitisedTags = tags.map((tag) => tag.replace(/{{|}}/g, ""));

  return uniq(sanitisedTags);
}

function extractMergeTagV2Fields(node: SerializedLexicalNode): string[] {
  const tags: string[] = [];

  // First pull out the merge tag nodes
  if (node.type === "merge-tag-v2" && "tag" in node) {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    tags.push(node.tag as string);
  }

  if (node.type === "design-huddle") {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const dhNode = node as SerializedDesignHuddleNode;
    if (dhNode.customisations?.mergeTags) tags.push(...dhNode.customisations.mergeTags);
  }

  // Recursively check children
  if ("children" in node && Array.isArray(node.children)) {
    for (const child of node.children) {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      tags.push(...extractMergeTagV2Fields(child as SerializedLexicalNode));
    }
  }

  return tags;
}

function extractEmailSubjectMergeFields(email_subject: string | null): string[] {
  const tags: string[] = [];

  if (email_subject) {
    const regex = /{{(.*?)}}/g;
    let match;

    while ((match = regex.exec(email_subject)) !== null) {
      tags.push(match[1]!.trim());
    }
  }

  return tags;
}
